/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../auth/apiClient";
import "./AuditPage.css";

const RANGE_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

export default function AuditPage() {
  const [tab, setTab] = useState("overview"); // overview | feed
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [feed, setFeed] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [filters, setFilters] = useState({
    actor_user_id: "",
    subject_user_id: "",
    event_type: "",
    event_prefix: "",
    q: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, d, t, et] = await Promise.all([
        apiFetch(`/api/admin/audit/analytics/summary?days=${days}`, {
          authRequired: true,
        }),
        apiFetch(`/api/admin/audit/analytics/daily?days=${days}`, {
          authRequired: true,
        }),
        apiFetch(`/api/admin/audit/analytics/top-pages?days=${days}`, {
          authRequired: true,
        }),
        apiFetch("/api/admin/audit/event-types", { authRequired: true }),
      ]);
      setSummary(s);
      setDaily(d || []);
      setTopPages(t || []);
      setEventTypes(et || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const loadFeed = useCallback(
    async (cursor) => {
      setLoading(true);
      setError("");
      try {
        const qs = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
          if (v) qs.append(k, v);
        });
        if (cursor) qs.append("cursor", cursor);
        qs.append("limit", "50");
        const data = await apiFetch(
          `/api/admin/audit?${qs.toString()}`,
          { authRequired: true }
        );
        setFeed((prev) => (cursor ? [...prev, ...data.items] : data.items));
        setNextCursor(data.next_cursor);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    if (tab === "overview") loadOverview();
    else loadFeed(null);
  }, [tab, loadOverview, loadFeed]);

  return (
    <div className="vsa-audit-shell">
      <div className="vsa-page-head">
        <div>
          <h1>Audit & Analytics</h1>
          <p className="vsa-page-subtitle">
            Every meaningful event in the system. Search by who did it, who it
            was done to, or by event type.
          </p>
        </div>
        <div className="vsa-audit-range">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {RANGE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="vsa-audit-tabs" role="tablist">
        <button
          className={tab === "overview" ? "active" : ""}
          onClick={() => setTab("overview")}
        >
          Overview
        </button>
        <button
          className={tab === "feed" ? "active" : ""}
          onClick={() => setTab("feed")}
        >
          Event feed
        </button>
      </div>

      {error && <div className="vsa-audit-error">{error}</div>}

      {tab === "overview" && summary && (
        <Overview
          summary={summary}
          daily={daily}
          topPages={topPages}
          eventTypes={eventTypes}
        />
      )}

      {tab === "feed" && (
        <Feed
          filters={filters}
          setFilters={setFilters}
          feed={feed}
          nextCursor={nextCursor}
          eventTypes={eventTypes}
          onApplyFilters={() => loadFeed(null)}
          onLoadMore={() => loadFeed(nextCursor)}
          loading={loading}
        />
      )}
    </div>
  );
}

function Overview({ summary, daily, topPages, eventTypes }) {
  return (
    <>
      <div className="vsa-audit-stats">
        <Stat label="Unique visitors" value={summary.unique_visitors} />
        <Stat label="Logged-in users" value={summary.unique_logged_in_users} />
        <Stat label="Successful logins" value={summary.logins} />
        <Stat
          label="Failed logins"
          value={summary.failed_logins}
          tone={summary.failed_logins > 5 ? "warn" : "neutral"}
        />
        <Stat label="Page views" value={summary.page_views} />
        <Stat label="Document views" value={summary.document_views} />
        <Stat label="Admin actions" value={summary.admin_actions} />
        <Stat label="Total events" value={summary.total_events} />
      </div>

      <DailyChart daily={daily} />

      <div className="vsa-audit-twocol">
        <section className="vsa-audit-card">
          <h2>Top pages</h2>
          {topPages.length === 0 ? (
            <p className="vsa-audit-empty">No page views recorded yet.</p>
          ) : (
            <table className="vsa-audit-table">
              <thead>
                <tr>
                  <th>Path</th>
                  <th>Views</th>
                  <th>Unique</th>
                </tr>
              </thead>
              <tbody>
                {topPages.map((p) => (
                  <tr key={p.path}>
                    <td>
                      <code>{p.path}</code>
                    </td>
                    <td>{p.hits.toLocaleString("en-IN")}</td>
                    <td>{p.unique_visitors.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="vsa-audit-card">
          <h2>Event types</h2>
          <table className="vsa-audit-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Count</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {eventTypes.slice(0, 15).map((e) => (
                <tr key={e.event_type}>
                  <td>
                    <code>{e.event_type}</code>
                  </td>
                  <td>{e.count.toLocaleString("en-IN")}</td>
                  <td>
                    {e.last_at
                      ? new Date(e.last_at).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  return (
    <div className={`vsa-audit-stat tone-${tone}`}>
      <div className="vsa-audit-stat-value">
        {value.toLocaleString("en-IN")}
      </div>
      <div className="vsa-audit-stat-label">{label}</div>
    </div>
  );
}

function DailyChart({ daily }) {
  if (daily.length === 0) {
    return (
      <section className="vsa-audit-card">
        <h2>Activity by day</h2>
        <p className="vsa-audit-empty">No data in this window.</p>
      </section>
    );
  }
  const max = Math.max(...daily.map((d) => d.events));
  return (
    <section className="vsa-audit-card">
      <h2>Activity by day</h2>
      <div className="vsa-audit-chart">
        {daily.map((d) => {
          const h = max > 0 ? (d.events / max) * 100 : 0;
          return (
            <div
              key={d.day}
              className="vsa-audit-chart-bar"
              title={`${d.day} — ${d.events} events · ${d.unique_visitors} unique`}
            >
              <div
                className="vsa-audit-chart-fill"
                style={{ height: `${Math.max(h, 2)}%` }}
              />
              <div className="vsa-audit-chart-label">
                {d.day.slice(5)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Feed({
  filters,
  setFilters,
  feed,
  nextCursor,
  eventTypes,
  onApplyFilters,
  onLoadMore,
  loading,
}) {
  return (
    <div className="vsa-audit-feed">
      <div className="vsa-audit-filters">
        <input
          type="text"
          placeholder="Search actor / subject / target…"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
        />
        <select
          value={filters.event_type}
          onChange={(e) =>
            setFilters({
              ...filters,
              event_type: e.target.value,
              event_prefix: "",
            })
          }
        >
          <option value="">Any event type</option>
          {eventTypes.map((e) => (
            <option key={e.event_type} value={e.event_type}>
              {e.event_type} ({e.count})
            </option>
          ))}
        </select>
        <select
          value={filters.event_prefix}
          onChange={(e) =>
            setFilters({
              ...filters,
              event_prefix: e.target.value,
              event_type: "",
            })
          }
        >
          <option value="">Any namespace</option>
          <option value="admin.">admin.*</option>
          <option value="auth.">auth.*</option>
          <option value="view.">view.*</option>
        </select>
        <input
          type="text"
          placeholder="Actor user_id (UUID)"
          value={filters.actor_user_id}
          onChange={(e) =>
            setFilters({ ...filters, actor_user_id: e.target.value })
          }
        />
        <input
          type="text"
          placeholder="Subject user_id (UUID)"
          value={filters.subject_user_id}
          onChange={(e) =>
            setFilters({ ...filters, subject_user_id: e.target.value })
          }
        />
        <button className="vsa-audit-apply" onClick={onApplyFilters}>
          Apply
        </button>
      </div>

      {feed.length === 0 && !loading && (
        <div className="vsa-audit-empty-card">No events match.</div>
      )}

      <table className="vsa-audit-table vsa-audit-feed-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Event</th>
            <th>Actor</th>
            <th>Subject</th>
            <th>Target</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {feed.map((r) => (
            <tr key={r.id}>
              <td>{new Date(r.created_at).toLocaleString()}</td>
              <td>
                <code>{r.event_type}</code>
              </td>
              <td>{r.actor_label || "—"}</td>
              <td>{r.subject_label || "—"}</td>
              <td>
                {r.target_label || (
                  <span className="vsa-audit-muted">—</span>
                )}
              </td>
              <td className="vsa-audit-detail">
                {Object.entries(r.data || {})
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ") || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {nextCursor && (
        <div className="vsa-audit-more">
          <button onClick={onLoadMore} disabled={loading}>
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
